import { useCallback } from 'react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  Button,
  Link,
  PromotionalBanner,
  Separator,
} from '@buffer-mono/popcorn'

import { QueueEmptyStateIllustration } from '~publish/components/PostTabs/QueueEmptyStateIllustration'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { channelSettings } from '~publish/legacy/routes'

import { useSelectedChannel } from '../ChannelContext'

import styles from './QueueWelcomeBanner.module.css'

export const QueueWelcomeBanner = (): JSX.Element | null => {
  const { isEnabled: isQueueWelcomeBannerEnabled, config } = useSplitEnabled<{
    bannerSignupDate: string
  }>('geid-set-my-posting-schedule')
  const accountSignUpAfter = new Date(config?.bannerSignupDate ?? '2025-12-01')

  const { isActive, dismiss } = useDismissableBanner('queueWelcomeBanner', {
    accountSignUpAfter,
  })
  const channel = useSelectedChannel()
  const navigate = useNavigate()

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>): void => {
      e.preventDefault()
      // Compute route at click time to capture current URL params
      const route = channelSettings.getRoute({ channelId: channel.id })
      navigate(route)
    },
    [channel.id, navigate],
  )

  if (!isActive || !isQueueWelcomeBannerEnabled) {
    return null
  }

  return (
    <>
      <PromotionalBanner variant="inline" className={styles.promotionalBanner}>
        <PromotionalBanner.Content>
          <PromotionalBanner.Heading size="large">
            Welcome to your Queue!
          </PromotionalBanner.Heading>
          <PromotionalBanner.Description color="subtle">
            <p>
              This is where your <strong>scheduled posts</strong> live.
              We&apos;ve set your <strong>posting times</strong> based on
              industry research and as you post more, we&apos;ll fine-tune them
              for your specific audience.
            </p>
            <p>
              <strong>Prefer to choose your own times?</strong>{' '}
              <Link className={styles.link} onClick={handleLinkClick}>
                Set a custom schedule.
              </Link>
            </p>
          </PromotionalBanner.Description>
          <PromotionalBanner.Actions>
            <Button onClick={dismiss}>Got It</Button>
          </PromotionalBanner.Actions>
        </PromotionalBanner.Content>
        <QueueEmptyStateIllustration />
      </PromotionalBanner>
      <Separator />
    </>
  )
}
